import * as React from "react";
import NoDataPlaceholder from "src/components/no-data-placeholder";
import { Box, Chip } from "@mui/material";
import styled from "styled-components";

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
`;

const Th = styled.th<{ width?: string }>`
    text-align: left;
    padding: 6px 8px;
    font-size: 11px;
    font-weight: 500;
    color: ${({ theme }) => theme.palette.secondary.main};
    border-bottom: 1px solid #585858;
    white-space: nowrap;
    ${({ width }) => width && `width: ${width};`}
`;

const Tr = styled.tr`
    &:hover {
        background: rgba(255, 255, 255, 0.04);
    }
`;

const Td = styled.td`
    padding: 5px 8px;
    border-bottom: 1px solid rgba(88, 88, 88, 0.4);
    vertical-align: middle;
    max-width: 300px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const statusColor = (val: string): "success" | "warning" | "error" | "default" => {
    const lower = (val ?? "").toLowerCase();
    if (lower === "done" || lower === "closed" || lower === "active") return "success";
    if (lower.startsWith("in progress") || lower === "in progress" || lower === "scope added")
        return "warning";
    if (lower === "to do" || lower === "open" || lower === "backlog") return "default";
    return "default";
};

const TemplateTable = ({
    data,
    parameters,
}: {
    data: Record<string, unknown>[] | null | undefined;
    parameters: {
        property: string;
        columns: {
            key: string;
            label: string;
            type: "string" | "number" | "chip";
            width?: string;
        }[];
    };
}) => {
    if (!data || data.length === 0) return <NoDataPlaceholder variant="list" errorMessage={null} />;

    return (
        <Box sx={{ overflow: "auto", width: "100%", height: "100%" }}>
            <Table>
                <thead>
                    <tr>
                        {parameters.columns.map((col) => (
                            <Th key={col.key} width={col.width}>
                                {col.label}
                            </Th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {data.map((row, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <Tr key={i}>
                            {parameters.columns.map((col) => {
                                const val = row[col.key];
                                let cellContent: React.ReactNode;
                                if (col.type === "chip") {
                                    cellContent = val ? (
                                        <Chip
                                            label={String(val)}
                                            size="small"
                                            color={statusColor(String(val))}
                                            variant="outlined"
                                            sx={{ fontSize: "10px", height: "18px" }}
                                        />
                                    ) : null;
                                } else if (col.type === "number") {
                                    cellContent = val != null ? Number(val).toLocaleString() : "—";
                                } else {
                                    cellContent = val != null ? String(val) : "—";
                                }
                                return <Td key={col.key}>{cellContent}</Td>;
                            })}
                        </Tr>
                    ))}
                </tbody>
            </Table>
        </Box>
    );
};

export default TemplateTable;
